import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { trackOnce, invoiceProperties, paidInvoiceEvent } from "/imports/libs/analytics";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import CheckoutTelrCard from "/imports/ui/views/CheckoutTelrCard";
import AddToCalendarMenu from "/imports/ui/components/shared/AddToCalendarMenu";

export default function CheckoutConfirmationView() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const widgetSlug = sessionStorage.getItem("widget_slug");
  const handleGoHome = () => navigate(widgetSlug ? `/${widgetSlug}` : "/");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const theme = useMemo(() => createWidgetTheme({ primaryColor: data?.widgetColor || undefined }), [data?.widgetColor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getBookingConfirmation(invoiceId);
        if (cancelled) return;
        setData(res);
        applyBranding({ name: res?.saas?.name, logoUrl: res?.saas?.logoUrl });
        // Covered in full by a package/voucher — a booking with no online payment.
        // Also where dashboard Pay Now lands, which settles a booking the club made.
        const props = invoiceProperties(res);
        trackOnce(`done_${invoiceId}`, paidInvoiceEvent(props), { ...props, paid_online: false });
      } catch (_err) {
        // surfaces as "could not load" via empty data
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // The credit that settled the booking lives on the invoice's bookish — surface
  // it so the client sees how the booking was covered rather than a payment line.
  const bookish = data?.invoice?.bookishs?.[0] || {};
  const usedPackage = bookish?.paidPlan?.type === "ppack";
  const usedVoucher = !!bookish?.voucher?._id && parseFloat(bookish?.voucher?.used || 0) > 0;
  let subtitle = "Your booking is confirmed";
  if (usedPackage && usedVoucher) subtitle = "Paid with your package and voucher";
  else if (usedPackage) subtitle = "Paid with your package";
  else if (usedVoucher) subtitle = "Paid with your voucher";

  const rightPaneContent = (
    <Box textAlign="center">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "5rem",
          height: "5rem",
          borderRadius: "50%",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
          mb: 2
        }}
      >
        <CheckCircleIcon sx={{ fontSize: "2.75rem", color: "primary.main" }} />
      </Box>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Booking confirmed
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {subtitle}
      </Typography>
      {/* Phone only: desktop shows it in the left column (CheckoutTelrCard's leftPaneContent). */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <AddToCalendarMenu calendar={data?.calendar} sx={{ mb: 1 }} />
      </Box>
      {/* Someone who got here from inside the widget has a club to go back to. An
          admin's mailed pay-link does not — it opens in a tab of its own, and
          "Back to Home" would drop the client somewhere they never came from.
          A booking settled from the dashboard is source "DASHBOARD" (the club
          created it), so settledVia is what marks it as the client's own doing. */}
      {data?.invoice?.source === "widget-v2" || data?.invoice?.settledVia === "WIDGET_SELF_SERVE" ? (
        <Button variant="contained" fullWidth onClick={handleGoHome}>
          Back to Home
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">
          You can now close this tab.
        </Typography>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CheckoutTelrCard data={data} rightPaneContent={rightPaneContent} leftPaneContent={<AddToCalendarMenu calendar={data?.calendar} />} />
    </ThemeProvider>
  );
}
